/**
 * countryConfig.ts — the single client-side source of truth for every
 * country-specific value (issue #378, owner decision 2026-07-15: the app is
 * global; nothing may be hardcoded for a specific country).
 *
 * Components read config via useCountryConfig(); non-React libs accept a
 * config (or individual values) as parameters defaulting to DEFAULT_COUNTRY.
 *
 * The DB source of truth is the admin-managed operational_countries /
 * operational_cities tables (Admin → Operational Regions). Fields the DB
 * doesn't carry yet fall back to the seed below. Uganda is the seeded default
 * so existing behaviour is unchanged.
 *
 * This file (and countryGeo.ts) are the ONLY places in src/ allowed to hold
 * country literals — enforced by scripts/assert-country-literal-ratchet.mjs.
 */

import { useEffect, useState, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useOperationalRegions, type OperationalCountry } from '@/hooks/useOperationalRegions';
import { CITIES_BY_COUNTRY } from '@/lib/countryGeo';
import {
  ANON_VIEWER,
  getMarketChoiceSnapshot,
  subscribeToMarketChoice,
  writeStoredMarketChoice,
  type StoredMarketChoice,
} from '@/lib/marketChoice';

export interface CountryConfig {
  code: string;                 // ISO-2, e.g. 'UG'
  name: string;                 // display name, e.g. 'Uganda'
  flagEmoji: string;
  currencyCode: string;         // ISO-4217, e.g. 'UGX'
  currencySymbol: string;
  locale: string;               // BCP-47, e.g. 'en-UG' — for Intl formatting
  timezone: string;             // IANA, e.g. 'Africa/Kampala'
  utcOffsetMinutes: number;
  phonePrefix: string;          // e.g. '+256'
  phoneNationalDigits: number;  // digits after the prefix
  phonePlaceholder: string;     // UI placeholder, e.g. '+256 700 123 456'
  phoneProviderHint: string;    // mobile-money provider prefixes copy
  localPhonePattern: RegExp;    // national format eligible for normalization
  usdExchangeRate: number;      // default; live rate comes from app settings
  mapCenter: { lat: number; lng: number };
  bounds: { minLat: number; maxLat: number; minLng: number; maxLng: number };
  geoSearchCode: string;        // lowercase code for geo APIs (nominatim/places)
  languages: string[];          // languages helpers commonly speak
  nationalIdLabel: string;
  nationalIdPattern: RegExp;
  nationalIdPlaceholder: string;
  policeCertValidityMonths: number;
  emergency: { police: string; ambulance: string };
  legal: { jurisdiction: string; dataProtectionLaw: string; regulator: string };
  support: { phone: string; whatsapp: string; location: string };
}

export const UGANDA: CountryConfig = {
  code: 'UG',
  name: 'Uganda',
  flagEmoji: '🇺🇬',
  currencyCode: 'UGX',
  currencySymbol: 'UGX',
  locale: 'en-UG',
  timezone: 'Africa/Kampala',
  utcOffsetMinutes: 3 * 60,
  phonePrefix: '+256',
  phoneNationalDigits: 9,
  phonePlaceholder: '+256 700 123 456',
  phoneProviderHint: 'MTN: 077, 078, 076, 039 · Airtel: 070, 075, 074',
  localPhonePattern: /^0[37][0-9]{8}$/,
  usdExchangeRate: 3700,
  mapCenter: { lat: 0.3476, lng: 32.5825 },
  bounds: { minLat: -1.6, maxLat: 4.3, minLng: 29.4, maxLng: 35.1 },
  geoSearchCode: 'ug',
  languages: ['English', 'Luganda', 'Swahili', 'Runyankore', 'Acholi', 'Ateso', 'Lugbara', 'Runyoro', 'Rukiga', 'Lusoga'],
  nationalIdLabel: 'National ID (NIN)',
  nationalIdPattern: /^[A-Z0-9]{14}$/i,
  nationalIdPlaceholder: 'Enter your National ID (14 characters)',
  policeCertValidityMonths: 6,
  emergency: { police: '999', ambulance: '0800 990 000' },
  legal: {
    jurisdiction: 'Uganda',
    dataProtectionLaw: "Uganda's Data Protection and Privacy Act 2019",
    regulator: 'Uganda Communications Commission',
  },
  support: { phone: '+256 700 123 456', whatsapp: '+256 700 123 456', location: 'Kampala, Uganda' },
};

/** The seeded default country. Swap the seed (or resolve from DB) — never hardcode elsewhere. */
export const DEFAULT_COUNTRY: CountryConfig = UGANDA;

/**
 * The seed's support numbers are stand-ins, not lines anyone answers. Until a
 * market sets real ones in its settings they are never dialled, opened in
 * WhatsApp or shown (WhatsApp Phase 0 review, gate 0B; lib/supportContactLinks).
 */
export const PLACEHOLDER_SUPPORT_NUMBERS: readonly string[] = [UGANDA.support.phone, UGANDA.support.whatsapp];

/** Full international phone pattern, derived from prefix + national digit count. */
export function fullPhonePattern(cfg: CountryConfig = DEFAULT_COUNTRY): RegExp {
  return new RegExp(`^\\${cfg.phonePrefix}[0-9]{${cfg.phoneNationalDigits}}$`);
}

/** Normalize a nationally-formatted number (e.g. 07XXXXXXXX) to international form. */
export function normalizePhone(phone: string, cfg: CountryConfig = DEFAULT_COUNTRY): string {
  const trimmed = (phone || '').replace(/\s/g, '');
  if (cfg.localPhonePattern.test(trimmed)) return cfg.phonePrefix + trimmed.slice(1);
  return trimmed;
}

export interface GeoBounds {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

/**
 * Country-agnostic bounds check (GPS remediation Phase 2C). Callers pass the
 * bounds of the ACTIVE booking/service market explicitly — there is no silent
 * Uganda default, so a travelling user's GPS can never move the commercial
 * market. Uses Number.isFinite (not truthiness): coordinates on the equator /
 * prime meridian (0) are valid.
 */
export function isWithinBounds(
  lat: number | null | undefined,
  lng: number | null | undefined,
  bounds: GeoBounds
): boolean {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  return (
    (lat as number) >= bounds.minLat &&
    (lat as number) <= bounds.maxLat &&
    (lng as number) >= bounds.minLng &&
    (lng as number) <= bounds.maxLng
  );
}

/**
 * True when coordinates fall inside a country's service bounds. The country
 * config is REQUIRED — pass the active booking/service market explicitly.
 */
export function isWithinServiceArea(
  lat: number | null | undefined,
  lng: number | null | undefined,
  cfg: CountryConfig
): boolean {
  return isWithinBounds(lat, lng, cfg.bounds);
}

/* Type-safe pickers for JSONB settings values (issue #378 phase 4). */
const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v : undefined);
const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
const strArr = (v: unknown): string[] | undefined =>
  Array.isArray(v) && v.length > 0 && v.every((x) => typeof x === 'string') ? (v as string[]) : undefined;
const obj = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});

/**
 * Merge an admin-managed operational_countries row over the seeded defaults.
 * Scalar columns win first; then the row's JSONB `settings` overrides any of
 * the recognised keys below (admin-editable without a deploy — phase 4 of
 * issue #378). Unknown/malformed settings values are ignored, so a bad admin
 * edit degrades to the seed instead of breaking the app.
 */
export function resolveCountryConfig(row?: OperationalCountry | null): CountryConfig {
  if (!row) return DEFAULT_COUNTRY;
  const code = row.code || DEFAULT_COUNTRY.code;
  const s = obj(row.settings);
  const sMap = obj(s.mapCenter);
  const sBounds = obj(s.bounds);
  const sEmergency = obj(s.emergency);
  const sLegal = obj(s.legal);
  const sSupport = obj(s.support);
  const base = DEFAULT_COUNTRY;
  const phonePrefix = row.phone_prefix || base.phonePrefix;

  // REVERTED (see below). A market added without explicit phone settings
  // inherits the seed's phone shape again.
  //
  // The previous attempt gave unconfigured markets a "neutral" digit count of
  // 15 minus the prefix, reasoning that it constrained nothing it could not
  // know. That reasoning was wrong, because `phoneNationalDigits` is not a
  // maximum: fullPhonePattern() interpolates it as an EXACT repetition count
  // in an anchored pattern — /^\+254[0-9]{12}$/ — so raising it from 9 to 12
  // did not widen the accepted set, it MOVED it. Every real 9-digit number in
  // those markets stopped validating, and a 12-digit string of arbitrary
  // digits started validating in its place. Mobile-money checkout, the default
  // payment method, became impossible in four live markets, and the one thing
  // that did newly pass was a number no provider can reach.
  //
  // Inheriting a wrong-but-plausible length is the lesser fault: it is right
  // for every 9-digit market and, where wrong, it truncates visibly at the
  // input rather than accepting an unreachable payout destination.
  //
  // The real defect is upstream and is NOT fixed here: activation requires only
  // a phone prefix, so a market can go live with no phone rules at all. Until
  // the shape is required at activation — or fullPhonePattern learns a range
  // for markets that have not declared one — there is no safe default to pick.
  // Tracked on #817.
  return {
    ...base,
    code,
    name: row.name || base.name,
    flagEmoji: row.flag_emoji || base.flagEmoji,
    currencyCode: row.currency_code || base.currencyCode,
    currencySymbol: row.currency_symbol || row.currency_code || base.currencySymbol,
    phonePrefix,
    locale: str(s.locale) ?? base.locale,
    timezone: str(s.timezone) ?? base.timezone,
    utcOffsetMinutes: num(s.utcOffsetMinutes) ?? base.utcOffsetMinutes,
    phoneNationalDigits: num(s.phoneNationalDigits) ?? base.phoneNationalDigits,
    phonePlaceholder: str(s.phonePlaceholder) ?? base.phonePlaceholder,
    phoneProviderHint: str(s.phoneProviderHint) ?? base.phoneProviderHint,
    usdExchangeRate: num(s.usdExchangeRate) ?? base.usdExchangeRate,
    mapCenter:
      num(sMap.lat) !== undefined && num(sMap.lng) !== undefined
        ? { lat: num(sMap.lat)!, lng: num(sMap.lng)! }
        : base.mapCenter,
    bounds:
      num(sBounds.minLat) !== undefined && num(sBounds.maxLat) !== undefined &&
      num(sBounds.minLng) !== undefined && num(sBounds.maxLng) !== undefined
        ? { minLat: num(sBounds.minLat)!, maxLat: num(sBounds.maxLat)!, minLng: num(sBounds.minLng)!, maxLng: num(sBounds.maxLng)! }
        : base.bounds,
    geoSearchCode: str(s.geoSearchCode) ?? code.toLowerCase(),
    languages: strArr(s.languages) ?? base.languages,
    nationalIdLabel: str(s.nationalIdLabel) ?? base.nationalIdLabel,
    nationalIdPlaceholder: str(s.nationalIdPlaceholder) ?? base.nationalIdPlaceholder,
    policeCertValidityMonths: num(s.policeCertValidityMonths) ?? base.policeCertValidityMonths,
    emergency: {
      police: str(sEmergency.police) ?? base.emergency.police,
      ambulance: str(sEmergency.ambulance) ?? base.emergency.ambulance,
    },
    legal: {
      jurisdiction: str(sLegal.jurisdiction) ?? (row.name || base.legal.jurisdiction),
      dataProtectionLaw: str(sLegal.dataProtectionLaw) ?? base.legal.dataProtectionLaw,
      regulator: str(sLegal.regulator) ?? base.legal.regulator,
    },
    support: {
      phone: str(sSupport.phone) ?? base.support.phone,
      whatsapp: str(sSupport.whatsapp) ?? base.support.whatsapp,
      location: str(sSupport.location) ?? base.support.location,
    },
  };
}

/** Resolve payment settings from the immutable booking snapshot. */
export function resolveBookingCountryConfig(
  countries: OperationalCountry[],
  countryCode: string | null | undefined,
  currencyCode: string | null | undefined,
  fallback: CountryConfig = DEFAULT_COUNTRY,
): { config: CountryConfig; resolved: boolean; legacyFallback: boolean } {
  const normalizedCountry = countryCode?.trim().toUpperCase();
  const normalizedCurrency = currencyCode?.trim().toUpperCase();
  const byCountry = normalizedCountry
    ? countries.find((country) => country.code.toUpperCase() === normalizedCountry)
    : undefined;
  if (byCountry) {
    const countryConfig = resolveCountryConfig(byCountry);
    const config = normalizedCurrency && normalizedCurrency !== countryConfig.currencyCode.toUpperCase()
      ? { ...countryConfig, currencyCode: normalizedCurrency, currencySymbol: normalizedCurrency }
      : countryConfig;
    return { config, resolved: true, legacyFallback: false };
  }

  const currencyMatches = normalizedCurrency
    ? countries.filter((country) => country.currency_code?.toUpperCase() === normalizedCurrency)
    : [];
  if (!normalizedCountry && currencyMatches.length === 1) {
    return { config: resolveCountryConfig(currencyMatches[0]), resolved: true, legacyFallback: true };
  }

  return {
    config: { ...fallback, currencyCode: normalizedCurrency || fallback.currencyCode },
    resolved: !normalizedCountry && !normalizedCurrency,
    legacyFallback: true,
  };
}

/**
 * React hook: the active country's config + its admin-managed city list.
 * Resolves from the primary operational country; falls back to the seed.
 */
export function useCountryConfig(): {
  config: CountryConfig;
  countries: OperationalCountry[];
  cities: string[];
  isLoading: boolean;
} {
  const { countries, getCityNames, primaryCountry, isLoading } = useOperationalRegions();
  const config = resolveCountryConfig(primaryCountry);
  const cities = getCityNames(config.code);
  return {
    config,
    countries,
    cities: cities.length > 0 ? cities : getCityNames(DEFAULT_COUNTRY.code),
    isLoading,
  };
}

/* ------------------------------------------------------------------------- *
 * Per-USER country resolution (issue #438). The platform-level primary
 * country above is an admin setting; a signed-in user in another operational
 * country must see their own country's copy and currency. Resolution order:
 *   1. explicit user override (the "Change" control — always wins)
 *   2. GPS, only when location permission is ALREADY granted (never prompts),
 *      matched against operational countries that carry real bounds
 *   3. the country saved on the user's profile
 *   4. the platform primary country
 * ------------------------------------------------------------------------- */

/* Market choice storage lives in marketChoice.ts — see that file for why it
   is separate (the auth layer clears it on sign-out, and this module imports
   useAuth, so keeping it here would make the two import each other). */

/** Bounds from a row's JSONB settings, or null when the admin hasn't set any. */
function rowBounds(row: OperationalCountry): { minLat: number; maxLat: number; minLng: number; maxLng: number } | null {
  const b = obj(obj(row.settings).bounds);
  const minLat = num(b.minLat), maxLat = num(b.maxLat), minLng = num(b.minLng), maxLng = num(b.maxLng);
  if (minLat === undefined || maxLat === undefined || minLng === undefined || maxLng === undefined) return null;
  return { minLat, maxLat, minLng, maxLng };
}

const canDetectLocation = () =>
  typeof navigator !== 'undefined' && !!navigator.geolocation && !!navigator.permissions;

/**
 * GPS country detection — silent: only runs if permission is already granted.
 * `pending` is true while that lookup could still change the answer (bounded
 * by the 10 s position timeout), so a caller can avoid acting on the fallback.
 */
function useDetectedCountryCode(countries: OperationalCountry[]): { code: string | null; pending: boolean } {
  const [code, setCode] = useState<string | null>(null);
  const [pending, setPending] = useState(canDetectLocation);
  useEffect(() => {
    if (!canDetectLocation()) { setPending(false); return; }
    let cancelled = false;
    setPending(true);
    const done = () => { if (!cancelled) setPending(false); };
    navigator.permissions
      .query({ name: 'geolocation' })
      .then((status) => {
        if (cancelled) return;
        if (status.state !== 'granted') { done(); return; }
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            if (cancelled) return;
            // SMALLEST matching box wins, not the first.
            //
            // National bounding boxes overlap at borders: northern Rwanda sits
            // inside Uganda's box, and Uganda's reaches into Kenya and
            // Tanzania. First-match handed those points to whichever country
            // sorted first — which, with Uganda at sort_order 1, meant every
            // Rwandan border town read as Ugandan. The smaller box is the more
            // specific claim, and resolves every one of these in favour of the
            // smaller country. Only reachable once all six markets had bounds
            // (20270160000000); before that only Uganda could ever match.
            const matches = countries
              .map((row) => ({ row, b: rowBounds(row) }))
              .filter((c): c is { row: OperationalCountry; b: NonNullable<ReturnType<typeof rowBounds>> } =>
                c.b !== null &&
                pos.coords.latitude >= c.b.minLat && pos.coords.latitude <= c.b.maxLat &&
                pos.coords.longitude >= c.b.minLng && pos.coords.longitude <= c.b.maxLng);
            const match = matches.sort(
              (x, y) =>
                (x.b.maxLat - x.b.minLat) * (x.b.maxLng - x.b.minLng) -
                (y.b.maxLat - y.b.minLat) * (y.b.maxLng - y.b.minLng),
            )[0]?.row;
            if (match) setCode(match.code);
            done();
          },
          done,
          { maximumAge: 60 * 60 * 1000, timeout: 10_000 }
        );
      })
      .catch(done);
    return () => {
      cancelled = true;
    };
  }, [countries]);
  return { code, pending };
}

/**
 * The country the signed-in user SHOPS in, if any.
 *
 * `home_country_code` first (20270173000000): the market a person browses,
 * sees prices in and books from follows where they LIVE. `country` /
 * `country_code` is the helper side of the same profile — the working and
 * payout market — and for anyone who is both helper and client (the owner's
 * rule: the two are not exclusive) those are different countries. A helper
 * living in one market and registered to work in another was being shown the
 * working market's copy and currency while shopping.
 *
 * `country` remains the fallback, and is still the only answer for the many
 * profiles predating the home columns; the backfill also left home_* NULL for
 * every helper on purpose, so this must degrade rather than blank out.
 *
 * Deliberately read-only and market-facing: currency, payouts and the
 * enforce_profile_country_currency trigger (20260725100000) stay bound to
 * country_code and are untouched by this.
 */
function useProfileCountry(): { country: string | null; isPending: boolean } {
  const { user } = useAuth();
  const { data, isPending } = useQuery({
    queryKey: ['profile-country', user?.id],
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    retry: false,
    queryFn: async (): Promise<string | null> => {
      const { data: row } = await supabase
        .from('profiles')
        .select('country, home_country_code')
        .eq('id', user!.id)
        .maybeSingle();
      // Trimmed truthiness, not `??`: a column added by ALTER TABLE and later
      // written by a form can hold '', and an empty string is "unanswered",
      // not "no country" — nullish coalescing would let it shadow `country`
      // and drop the user to the platform default.
      return row?.home_country_code?.trim() || row?.country || null;
    },
  });
  return { country: data ?? null, isPending: !!user?.id && isPending };
}

export type UserCountrySource = 'override' | 'gps' | 'profile' | 'platform';

/**
 * React hook: the country config for the CURRENT USER (see resolution order
 * above), plus a setter for the always-available manual override.
 */
export function useUserCountryConfig(): {
  config: CountryConfig;
  countries: OperationalCountry[];
  cities: string[];
  source: UserCountrySource;
  setCountryOverride: (code: string | null) => void;
  /**
   * The market this viewer would be in if they had never switched — their
   * account's country, or the platform default. `null` when that is the
   * market they are already in, so a caller can render "you are browsing
   * another market, switch back" without working out the comparison itself.
   *
   * `source` has existed since #438 to let the UI disclose an override and
   * had no consumers, which is why an overridden market was invisible.
   */
  homeCountry: CountryConfig | null;
  isLoading: boolean;
  /**
   * False while the viewer's market could still change: the market list is
   * loading, or (unless an explicit choice outranks them) the session is
   * still restoring or a signed-in viewer's account country has not arrived. Until then `config` is the platform
   * default, fine to render but not to act on (e.g. a tel: link to another
   * market's support line).
   */
  marketSettled: boolean;
} {
  const { countries, getCityNames, primaryCountry, isLoading } = useOperationalRegions();
  // isLoading, not raw loading: it also covers a slow ("degraded") restore,
  // where loading is already false but the user may still arrive.
  const { user, isLoading: authLoading } = useAuth();
  const viewer = user?.id ?? ANON_VIEWER;
  // Subscribed, not copied into local state: every component that reads the
  // market must see the same one, or switching in the banner leaves the page
  // behind it on the old market.
  const stored = useSyncExternalStore(subscribeToMarketChoice, getMarketChoiceSnapshot, getMarketChoiceSnapshot);
  const { code: gpsCode, pending: gpsPending } = useDetectedCountryCode(countries);
  const { country: profileCountry, isPending: profilePending } = useProfileCountry();

  // Only the person who made the choice is held to it.
  const override = stored && stored.forUser === viewer ? stored.code : null;

  const findCountry = (key: string | null): OperationalCountry | null => {
    if (!key) return null;
    const k = key.trim().toLowerCase();
    return countries.find((c) => c.code.toLowerCase() === k || c.name.toLowerCase() === k) ?? null;
  };

  /*
   * Order: an explicit choice, then the country on the account, then a
   * detected one, then the platform default.
   *
   * GPS sits BELOW the profile deliberately, where it used to sit above it.
   * No major marketplace lets an inferred location silently overrule a
   * country the user has actually stated — Airbnb, Booking.com, Netflix and
   * Amazon all resolve the market server-side and keep device location behind
   * an explicit control. With bounds now defined for all six markets
   * (20270160000000), leaving GPS on top would have started silently moving
   * signed-in users between markets the moment they travelled.
   */
  let source: UserCountrySource = 'platform';
  let row = findCountry(override);
  if (row) source = 'override';
  if (!row) { row = findCountry(profileCountry); if (row) source = 'profile'; }
  if (!row) { row = findCountry(gpsCode); if (row) source = 'gps'; }
  if (!row) row = primaryCountry ?? null;

  const setCountryOverride = (code: string | null) => {
    const next: StoredMarketChoice | null = code ? { code, forUser: viewer } : null;
    // The write notifies every subscriber, so no local state to keep in step.
    writeStoredMarketChoice(next);
  };

  const config = resolveCountryConfig(row);
  const operationalCities = getCityNames(config.code);
  const cities = operationalCities.length > 0
    ? operationalCities
    : (CITIES_BY_COUNTRY[config.code] ?? []);

  // Where this viewer belongs when nothing is overriding it. Only reported
  // when it differs from what they are looking at — the caller's job is to
  // offer the way back, not to compute whether one is needed.
  const homeRow = findCountry(profileCountry) ?? primaryCountry ?? null;
  const homeConfig = homeRow ? resolveCountryConfig(homeRow) : null;
  const homeCountry =
    homeConfig && homeConfig.code !== config.code ? homeConfig : null;

  // While the session is restoring, user is null, so "no profile query" does
  // not yet mean "signed out".
  // Not even an override settles it during the restore: until the user is
  // known, `viewer` is anonymous and a signed-out choice can look like theirs
  // (a signed-out choice must not outrank a signed-in account).
  // A pending GPS lookup only matters when nothing above it has decided.
  const marketSettled =
    !isLoading && !authLoading &&
    (source === 'override' || (!profilePending && (source === 'profile' || !gpsPending)));

  return { config, countries, cities, source, setCountryOverride, homeCountry, isLoading, marketSettled };
}
